import {Component, Inject} from '@angular/core';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Field} from '@app/interfaces';
import {FormHelperService} from '@app/util/form-helper.service';
import {NotificationService} from '@app/services/notification.service';
import {VehicleExpensesIncomesService} from '@app/services/vehicles/vehicle-expenses-incomes.service';
import {Subscription} from 'rxjs';

export interface V2ExpenseFormDialogData {
  /** entityApi null → form mode: nothing is saved, the edited row is handed
   *  back as `{value, shouldReopen}` for the caller's own form (BRIEFD-7558).
   *  entityUuid is then null too. */
  entityUuid: string | null;
  entityApi: { patchField: (entityUuid: string, fieldUuid: string, values: unknown) => any } | null;
  field: Field<string>;
  item: any | null;
}

/**
 * Design 2.0 expense add/edit dialog.
 *
 * Uses the shared `<app-v2-modal>` shell for chrome and the BE-driven
 * `<app-form-input-repeater>` engine for the form body — so form fields
 * (dropdowns, dates, amounts, files, validation) stay consistent with the
 * rest of the app and upload/persist through their established pipelines.
 */
@Component({
  selector: 'app-v2-expense-form-dialog',
  templateUrl: './v2-expense-form-dialog.component.html',
  styleUrls: ['./v2-expense-form-dialog.component.scss'],
  standalone: false,
})
export class V2ExpenseFormDialogComponent {
  public field: Field<string>;
  public item: any;
  public isSubmitting = false;
  public isEditingExistingItem = false;
  public formGroup: FormGroup;
  /** The same dialog serves both the expense and the income repeater, so its
   *  chrome follows whichever one it was opened with. */
  public title = '';
  public subtitle = '';

  private entityApi: V2ExpenseFormDialogData['entityApi'];
  private entityUuid: string | null;
  private subscription = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: V2ExpenseFormDialogData,
    private formBuilder: FormBuilder,
    private formHelperService: FormHelperService,
    private expensesService: VehicleExpensesIncomesService,
    public dialogRef: MatDialogRef<unknown>,
    private notificationService: NotificationService,
  ) {
    this.entityApi = data.entityApi;
    this.entityUuid = data.entityUuid;

    const controls: Record<string, any> = {};
    controls[data.field.fieldUuid] = this.formHelperService.buildRepeater(
      {...data.field},
      this.formBuilder.array([]),
    );
    this.formGroup = this.formBuilder.group(controls);

    if (data.item) {
      const items = (this.formGroup.controls[data.field.fieldUuid] as FormArray).controls;
      // A row added in the vehicle form has no uuid until the vehicle is
      // saved, so those are found by position (buildRepeater keeps the order).
      this.item = data.item.wrappedItemUuid
        ? items.find((el: any) => el.controls.wrappedItemUuid.value === data.item.wrappedItemUuid)
        : items[(data.field.value as unknown as any[]).indexOf(data.item)];
      this.isEditingExistingItem = true;
    }

    this.field = {...data.field};

    // Never let a lost edit target silently turn into a duplicate add.
    if (data.item && !this.item) {
      this.notificationService.showErrorAlert();
      queueMicrotask(() => this.dialogRef.close(false));
    }

    if (!this.item) {
      this.item = this.formHelperService.prepareEmptyRepeaterItem(this.field, true);
      (this.formGroup.controls[this.field.fieldUuid] as FormArray).push(this.item);
    }

    const isIncome = data.field.config?.typeId === 'briefd.vehicle.expenses.income_repeater';
    this.title = isIncome ? $localize`Prilivi` : $localize`Stroški`;
    this.subtitle = isIncome
      ? (this.isEditingExistingItem ? $localize`Uredi priliv vozila` : $localize`Dodaj priliv vozila`)
      : (this.isEditingExistingItem ? $localize`Uredi strošek vozila` : $localize`Dodaj strošek vozila`);

    // Auto-fill title from selected type (matches legacy expense UX).
    this.subscription.add(this.expensesService.onIncomeOrExpenseFormSetup(this.field, this.formGroup, this.item));
  }

  static open(
    dialog: MatDialog,
    entityUuid: string | null,
    entityApi: V2ExpenseFormDialogData['entityApi'],
    field: Field<string>,
    item: any | null,
  ): MatDialogRef<V2ExpenseFormDialogComponent> {
    return dialog.open(V2ExpenseFormDialogComponent, {
      panelClass: 'v2-modal-panel',
      backdropClass: 'v2-modal-backdrop',
      hasBackdrop: true,
      autoFocus: false,
      // The v2-modal shell controls its own max-width via `size="lg"` (640px).
      // Without overriding width, MatDialog uses the global default
      // (960px, set in app.module.ts), so the pane sits ~320px wider than
      // the visible modal and the modal reads as left-shifted. Match the
      // pane to the inner modal's max-width.
      width: '680px',
      maxWidth: '95vw',
      data: {entityUuid, entityApi, field, item},
    });
  }

  close(): void {
    this.subscription.unsubscribe();
    this.dialogRef.close(false);
  }

  save(shouldReopen: boolean): void {
    this.isSubmitting = true;
    this.formGroup.disable();

    if (!this.entityApi) {
      this.subscription.unsubscribe();
      this.dialogRef.close({value: this.item.getRawValue(), shouldReopen});
      return;
    }

    const values = this.formHelperService.getValuesFromTabbedForm(this.formGroup);

    this.entityApi.patchField(this.entityUuid!, this.field.fieldUuid, values).subscribe(
      (result: any) => {
        this.subscription.unsubscribe();
        this.dialogRef.close({result, shouldReopen});
      },
      () => {
        this.isSubmitting = false;
        this.formGroup.enable();
        this.notificationService.showErrorAlert();
      },
    );
  }
}
